import React from "react";
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import {
  BsFillCreditCardFill,
  BsQuestionSquareFill,
  BsQuestionCircleFill,
  BsChatDots,
  BsArrowRepeat,
  BsShieldFill,
  BsBriefcaseFill,
  BsPeopleFill,
} from "react-icons/bs";
import { FiChevronsLeft, FiChevronsRight, FiX } from "react-icons/fi";

const ADMIN_NAV_ITEMS = [
  { to: "/admin", label: "Messages", icon: BsChatDots, end: true },
  { to: "/admin/payments", label: "Payments Section", icon: BsFillCreditCardFill },
  { to: "/admin/claims", label: "Claim Section", icon: BsQuestionSquareFill },
  { to: "/admin/refer-and-earn", label: "Refer & Earn", icon: BsArrowRepeat },
  { to: "/admin/faq-update", label: "FAQ Update", icon: BsQuestionCircleFill },
  { to: "/admin/careers", label: "Careers", icon: BsBriefcaseFill },
  { to: "/admin/leadership-team", label: "Leadership Team", icon: BsPeopleFill },
];

const AdminSidebarNavItems = ({ collapsed, isMobile, onNavClick }) => {
  const location = useLocation();
  const showLabels = !collapsed || isMobile;

  return (
    <List sx={{ pt: 0, pb: 0 }}>
      {ADMIN_NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const isActive = item.end
          ? location.pathname === item.to
          : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

        const link = (
          <ListItem
            button
            component={Link}
            to={item.to}
            onClick={onNavClick}
            className={isActive ? "user-sidebar-item--active" : ""}
            sx={{
              color: "#ebedef",
              px: showLabels ? 2 : 1,
              justifyContent: showLabels ? "flex-start" : "center",
              "&:hover, &.user-sidebar-item--active": {
                backgroundColor: "#1D201F",
                color: "#fff",
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: showLabels ? 40 : 0,
                justifyContent: "center",
              }}
            >
              <Icon />
            </ListItemIcon>
            {showLabels && <ListItemText primary={item.label} />}
          </ListItem>
        );

        if (!showLabels) {
          return (
            <Tooltip key={item.to} title={item.label} placement="right" arrow>
              <span className="user-sidebar-tooltip-wrap">{link}</span>
            </Tooltip>
          );
        }

        return <React.Fragment key={item.to}>{link}</React.Fragment>;
      })}
    </List>
  );
};

const AdminSidebar = ({
  variant = "temporary",
  open = false,
  collapsed = true,
  onClose,
  onNavClick,
  onToggleExpanded,
}) => {
  const showProfileDetails = !collapsed || variant === "temporary";

  const profileBlock = (
    <div className="user-dashboard-sidebar__profile">
      <div className="sidebar-profile-card">
        <div className="sidebar-profile-card__initials">
          <BsShieldFill size={20} />
        </div>
        {showProfileDetails && (
          <div className="sidebar-profile-card__info">
            <p className="sidebar-profile-card__name">Admin</p>
            <p className="sidebar-profile-card__subtitle">User Dashboard</p>
          </div>
        )}
      </div>
    </div>
  );

  const sidebarBody = (
    <>
      <div className="user-dashboard-sidebar__header">
        {variant === "permanent" ? (
          <button
            type="button"
            className="sidebar-toggle-btn"
            onClick={onToggleExpanded}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <FiChevronsRight size={18} /> : <FiChevronsLeft size={18} />}
          </button>
        ) : (
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close menu"
          >
            <FiX size={20} />
          </button>
        )}
      </div>
      <nav className="user-dashboard-sidebar__nav">
        <AdminSidebarNavItems
          collapsed={collapsed}
          isMobile={variant === "temporary"}
          onNavClick={onNavClick}
        />
      </nav>
      {profileBlock}
    </>
  );

  if (variant === "permanent") {
    return <aside className="user-dashboard-sidebar admin-dashboard-sidebar">{sidebarBody}</aside>;
  }

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      variant="temporary"
      PaperProps={{ className: "user-dashboard-sidebar user-dashboard-sidebar--mobile admin-dashboard-sidebar" }}
    >
      {sidebarBody}
    </Drawer>
  );
};

export default AdminSidebar;
